/**
 * FeedbackButton
 *
 * Fixed bottom-right button that opens a small modal for reporting a bug,
 * flagging confusion, sending a suggestion, or leaving praise. Every submission
 * is tied to the signed-in user server-side; the modal only asks for the kind
 * and the free-text.
 *
 * Preview mode (VITE_STATIC_PREVIEW=1): the button is shown but clicking it
 * opens a short "sign in to send feedback" note instead of the real form.
 * Everything else stays the same so we can review the placement visually.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type ApiError } from '../lib/api';

type Kind = 'bug' | 'confusing' | 'suggestion' | 'praise';

const KINDS: Array<{ id: Kind; label: string; hint: string }> = [
  { id: 'bug', label: 'Bug', hint: 'Something is broken.' },
  { id: 'confusing', label: 'Confusing', hint: 'I got lost or the wording was unclear.' },
  { id: 'suggestion', label: 'Suggestion', hint: 'An idea for how to make this better.' },
  { id: 'praise', label: 'Praise', hint: 'Something worked well.' },
];

const IS_PREVIEW = import.meta.env.VITE_STATIC_PREVIEW === '1';

interface Props {
  lessonSlug: string;
}

export function FeedbackButton({ lessonSlug }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 flex items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-2 text-xs font-medium text-text shadow-lg shadow-black/40 transition-colors hover:border-amber hover:text-amber focus:outline-none focus:ring-2 focus:ring-amber/60 md:bottom-6 md:right-6"
        aria-label="Send feedback about this lesson"
      >
        <FeedbackIcon />
        Feedback
      </button>
      {open ? <FeedbackModal lessonSlug={lessonSlug} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function FeedbackIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function FeedbackModal({ lessonSlug, onClose }: { lessonSlug: string; onClose: () => void }) {
  const [kind, setKind] = useState<Kind>('suggestion');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const maxLen = 2000;
  const remaining = useMemo(() => maxLen - body.length, [body.length, maxLen]);
  const disabled = submitting || body.trim().length === 0;

  const submit = useCallback(async () => {
    if (disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      if (IS_PREVIEW) {
        // In the static preview we don't have an auth cookie yet. Simulate success
        // so reviewers can see the confirmation state without wiring auth first.
        await new Promise((resolve) => setTimeout(resolve, 250));
        setDone(true);
        return;
      }
      await api.post<{ ok: true; id: string }>('/api/feedback', {
        lessonSlug,
        kind,
        body: body.trim(),
      });
      setDone(true);
    } catch (err) {
      const e = err as ApiError;
      if (e && typeof e === 'object' && typeof e.error === 'string') {
        if (e.error === 'http_401' || e.error === 'unauthorized') {
          setError('You need to sign in before sending feedback.');
        } else if (e.error === 'rate_limited') {
          const msg = typeof e.message === 'string' ? e.message : null;
          setError(msg ?? 'You just sent feedback. Give it a few minutes before sending more.');
        } else if (e.error === 'invalid_body' || e.error === 'invalid_slug' || e.error === 'invalid_json') {
          setError('Feedback needs a message and a valid lesson. Try again.');
        } else {
          setError('Something went wrong on our end. Try again in a moment.');
        }
      } else {
        setError('Network hiccup. Check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [body, disabled, kind, lessonSlug]);

  // Close on Escape. Focus the textarea on open.
  useEffect(() => {
    textareaRef.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 p-4 backdrop-blur-sm md:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className="w-full max-w-md rounded-xl border border-line bg-surface p-5 shadow-2xl shadow-black/60"
      >
        {done ? (
          <ThanksState onClose={onClose} />
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="feedback-title" className="text-base font-semibold text-text">
                  Send feedback
                </h2>
                <p className="mt-1 text-xs text-muted">
                  Lesson {lessonSlug}. Tied to your account so we can follow up.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-2 py-1 text-xs text-muted hover:border-amber hover:text-amber"
                aria-label="Close"
              >
                Close
              </button>
            </div>

            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-widest text-muted">
                What kind?
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {KINDS.map((k) => {
                  const active = kind === k.id;
                  return (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => setKind(k.id)}
                      className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                        active
                          ? 'border-amber bg-amber/10 text-amber'
                          : 'border-line bg-elevated text-text hover:border-amber/60'
                      }`}
                    >
                      <div className="font-medium">{k.label}</div>
                      <div className={`mt-0.5 text-[11px] ${active ? 'text-amber/80' : 'text-muted'}`}>
                        {k.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="feedback-body" className="text-xs font-medium uppercase tracking-widest text-muted">
                Details
              </label>
              <textarea
                id="feedback-body"
                ref={textareaRef}
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, maxLen))}
                placeholder="What happened, what you expected, or what you liked. Be as specific as you can."
                rows={5}
                className="mt-1.5 w-full rounded-md border border-line bg-ink/60 p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
              />
              <div className="mt-1 flex justify-end text-[11px] text-muted">
                {remaining} characters left
              </div>
            </div>

            {error ? (
              <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-300">
                {error}
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-2 text-xs text-muted hover:border-amber hover:text-amber"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={disabled}
                className="rounded-md bg-amber px-3.5 py-2 text-xs font-semibold text-ink transition-colors hover:bg-amber-hover disabled:cursor-not-allowed disabled:bg-amber/40"
              >
                {submitting ? 'Sending...' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ThanksState({ onClose }: { onClose: () => void }) {
  return (
    <div className="py-4 text-center">
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-amber/40 bg-amber/10 text-amber">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="mt-3 text-base font-semibold text-text">Thanks, got it.</h2>
      <p className="mt-1 text-xs text-muted">
        Marshall reads every one of these. If you flagged a bug, expect a quick reply.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-4 rounded-md border border-line px-3 py-2 text-xs text-muted hover:border-amber hover:text-amber"
      >
        Close
      </button>
    </div>
  );
}
